import React, { useCallback, useEffect, useRef } from 'react';

import { isFirstPartyLink } from '@confluence/content-smartlinks/entry-points/smartLinkBatchHelpers.ts';
import { PERSISTED_KEYS_ON_SERVER } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';

import {
	GOOGLE_DRIVE_PROVIDER_KEY,
	useSmartlinkConnectionLinkCount,
} from '../../banners/hooks/useSmartlinkConnectionLinkCount.ts';
import type { EnhancedSmartLink } from '../../utils/smartLinkTypes.ts';
import { GdriveConnectorPromoBanner } from '../GdriveConnectorPromoBanner.tsx';
import {
	GDRIVE_ANALYTICS_CONNECTOR,
	getBannerDismissedEvent,
	getBannerShownEvent,
	type GdriveIneligibleReason,
} from '../promoBannerAnalytics.ts';

import { useBannerDismissal } from './useBannerDismissal.ts';
import { useGdriveSmartLinkAuth } from './useGdriveSmartLinkAuth.ts';
import { useTwgAnalyticsEvents } from '../../useTwgAnalyticsEvents.ts';

export type UseGdrivePromoBannerInput = {
	links: EnhancedSmartLink[];
	contentId: string | undefined;
};

export type UseGdrivePromoBannerResult = {
	element: React.ReactElement | null;
	/** Ineligibility reason; `undefined` while loading or when shown. Read by the footer. */
	ineligibleReason: GdriveIneligibleReason | undefined;
};

const SHOWN_ATTRIBUTES = {
	bannerType: 'gdrivePromo',
	connector: GDRIVE_ANALYTICS_CONNECTOR,
} as const;

export const useGdrivePromoBanner = ({
	links,
	contentId,
}: UseGdrivePromoBannerInput): UseGdrivePromoBannerResult => {
	const { createAnalyticsEvent } = useTwgAnalyticsEvents();

	const isPageFirstPartyOnly = links.length > 0 && links.every((l) => isFirstPartyLink(l.url));

	const { value: linkCounts, loading: isLinkCountLoading } = useSmartlinkConnectionLinkCount({
		skip: !isPageFirstPartyOnly,
	});
	const hasSiteWideDriveLinks = (linkCounts?.[GOOGLE_DRIVE_PROVIDER_KEY] ?? 0) >= 1;

	const {
		authUrl,
		isAuthenticated,
		isLoading: isAuthLoading,
		isConnecting,
		connect,
	} = useGdriveSmartLinkAuth();

	const { isDismissed, dismiss } = useBannerDismissal(
		PERSISTED_KEYS_ON_SERVER.PERSISTED_BYLINE_CONNECTIONS_GDRIVE_BANNER_DISMISSED_PAGES,
		contentId,
	);

	const isLoading = isLinkCountLoading || isAuthLoading;

	// First terminal failure in check order; loading yields `undefined` (not reported).
	let ineligibleReason: GdriveIneligibleReason | undefined;
	if (!isPageFirstPartyOnly) {
		ineligibleReason = 'notFirstPartyOnly';
	} else if (isLoading) {
		ineligibleReason = undefined;
	} else if (!hasSiteWideDriveLinks) {
		ineligibleReason = 'noSiteWideDriveLinks';
	} else if (isAuthenticated) {
		ineligibleReason = 'alreadyAuthenticated';
	} else if (isDismissed) {
		ineligibleReason = 'dismissed';
	} else if (!authUrl) {
		ineligibleReason = 'noAuthUrl';
	}

	const shouldShow = !isLoading && ineligibleReason === undefined;

	const hasFiredShownRef = useRef(false);
	useEffect(() => {
		if (!shouldShow || hasFiredShownRef.current) {
			return;
		}
		hasFiredShownRef.current = true;
		createAnalyticsEvent(getBannerShownEvent(SHOWN_ATTRIBUTES)).fire();
	}, [shouldShow, createAnalyticsEvent]);

	const handleDismiss = useCallback(() => {
		createAnalyticsEvent(getBannerDismissedEvent(SHOWN_ATTRIBUTES)).fire();
		dismiss();
	}, [createAnalyticsEvent, dismiss]);

	if (!shouldShow) {
		return { element: null, ineligibleReason };
	}

	return {
		element: (
			<GdriveConnectorPromoBanner
				onConnect={connect}
				onDismiss={handleDismiss}
				isConnecting={isConnecting}
			/>
		),
		ineligibleReason: undefined,
	};
};
